import { useEffect, useMemo, useRef } from 'react';
import { useDebounce } from 'use-debounce';

import { useFeatureDetails, useViewer } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

/**
 * Course recommendations for the current conversation. Only queries when the
 * workspace has GoodHabitz and AI, the viewer is a participant, and the
 * caller is ready (e.g. the sidebar section is open). Cache-first so open/close
 * does not re-run AI; refetch only when answers actually change. Last-fetched
 * revision is stored per conversation so a closed section or a conversation
 * switch cannot mark unread answers as already fetched. Last-fetched
 * revision is stored per conversation so a closed section or a conversation
 * switch cannot mark unread answers as already fetched.
 * @param input Conversation to recommend for, plus whether the query should wait.
 * @param input.evaluationSubmissionID Conversation id, once known.
 * @param input.skip True while the sidebar section is still closed.
 */
export default function useConversationLearningRecommendations(input: {
	evaluationSubmissionID?: string;
	skip?: boolean;
}) {
	const viewer = useViewer();
	const goodHabitzFeature = useFeatureDetails(graphql.FeatureName.Goodhabitz);
	const aiFeature = useFeatureDetails(graphql.FeatureName.Ai);

	const submissionQuery = graphql.useEvaluationSubmissionQuery({
		variables: { id: input.evaluationSubmissionID ?? '' },
		skip: !input.evaluationSubmissionID,
		fetchPolicy: 'cache-first',
	});

	const submission = submissionQuery.data?.evaluationSubmission;
	const viewerID = viewer?.id;
	const viewerIsParticipant =
		Boolean(viewerID) &&
		(viewerID === submission?.employee.user.id || viewerID === submission?.manager.user.id);

	const answersRevision = useMemo(
		() =>
			JSON.stringify({
				employee: submission?.employee.sections,
				manager: submission?.manager.sections,
			}),
		[submission],
	);
	const [debouncedAnswersRevision] = useDebounce(answersRevision, 800);

	const canQuery =
		!input.skip &&
		!goodHabitzFeature.loading &&
		!aiFeature.loading &&
		Boolean(goodHabitzFeature.available) &&
		Boolean(aiFeature.available) &&
		Boolean(input.evaluationSubmissionID) &&
		viewerIsParticipant;

	const {
		data,
		loading,
		refetch: refetchRecommendations,
	} = graphql.useConversationLearningRecommendationsQuery({
		variables: {
			input: {
				evaluationSubmissionID: input.evaluationSubmissionID ?? '',
			},
		},
		skip: !canQuery,
		fetchPolicy: 'cache-first',
	});

	const lastFetchedAnswersRevisionByID = useRef(new Map<string, string>());
	const answersRevisionRef = useRef(answersRevision);
	answersRevisionRef.current = answersRevision;

	useEffect(() => {
		const submissionID = input.evaluationSubmissionID;
		if (!submissionID || !canQuery) {
			return;
		}

		const lastFetched = lastFetchedAnswersRevisionByID.current.get(submissionID);

		// First query for this conversation: `skip: false` already fires it.
		// Do not seed while closed or before the submission is loaded — that
		// marked unread answers as fetched and later caused a stale cache hit
		// or a second AI call once the debounce caught up.
		if (lastFetched === undefined) {
			lastFetchedAnswersRevisionByID.current.set(submissionID, answersRevisionRef.current);
			return;
		}

		if (lastFetched === debouncedAnswersRevision) {
			return;
		}

		lastFetchedAnswersRevisionByID.current.set(submissionID, debouncedAnswersRevision);
		void refetchRecommendations();
	}, [canQuery, debouncedAnswersRevision, input.evaluationSubmissionID, refetchRecommendations]);

	return {
		available: Boolean(goodHabitzFeature.available && aiFeature.available) && viewerIsParticipant,
		loading: goodHabitzFeature.loading || aiFeature.loading || loading,
		recommendations: canQuery
			? (data?.conversationLearningRecommendations.recommendations ?? [])
			: [],
	};
}
